import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../Store/store';

interface PageTermsProps {
  onRequestClose?: () => void;
}

export const PageTerms: React.FC<PageTermsProps> = ({ onRequestClose }) => {
  const { currentGame } = useSelector((state: RootState) => state.gameSession);
  const textColor = currentGame?.colorSecondary || 'white';
  const linkColor = currentGame?.colorSecondary || '#66b3ff';

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto', color: textColor, fontSize: '0.95rem', lineHeight: '1.6' }}>
      <div style={{ padding: '0 10px', marginBottom: '60px' }}>
        <p style={{ fontStyle: 'italic', opacity: 0.7, fontSize: '0.9rem', marginBottom: '1.5rem' }}>
          Last Updated: February 1st, 2026
        </p>
        
        <p style={{ opacity: 0.7, marginBottom: '1rem' }}>
          The following is the terms of service and privacy policy.
        </p>
        
        <p style={{ marginBottom: '2rem', fontSize: '1rem' }}>
          Welcome to the games and websites operated by <strong style={{ color: textColor, fontWeight: 600 }}>Mental Mint LLC</strong> ("we," "us," "our").
          By using any of our games, websites, or related services (the "Services"), you agree to the following Terms of Service and Privacy Policy.
          If you do not agree, please discontinue use of the Services.
        </p>

        <section style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.2rem', marginBottom: '0.8rem', marginTop: 0, color: textColor }}>1. Definitions</h2>
          <p><strong style={{ fontWeight: 600 }}>"Services"</strong> include all games, websites, and online features offered by Mental Mint LLC.</p>
          <p><strong style={{ fontWeight: 600 }}>"User," "you," or "your"</strong> means any person who accesses or uses our Services.</p>
          <p><strong style={{ fontWeight: 600 }}>"Content"</strong> means all text, graphics, code, and gameplay materials within the Services.</p>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.2rem', marginBottom: '0.8rem', marginTop: 0, color: textColor }}>2. License to Use</h2>
          <p>We grant you a limited, non-exclusive, non-transferable, revocable license to access and use our Services for personal, non-commercial entertainment.</p>
          <p>You may not:</p>
          <ul style={{ margin: '0.5rem 0 1rem 1.5rem', padding: 0 }}>
            <li>Copy, modify, distribute, or resell any part of our Services;</li>
            <li>Reverse engineer or attempt to extract source code;</li>
            <li>Use automated tools (bots, scrapers, or scripts) that disrupt gameplay or data collection.</li>
          </ul>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.2rem', marginBottom: '0.8rem', marginTop: 0, color: textColor }}>3. Intellectual Property</h2>
          <p>
            All games, artwork, code, design, and related materials are the exclusive property of Mental Mint LLC or its licensors and are protected by U.S. and international laws.
            You may not use our names, logos, or branding without written permission.
          </p>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.2rem', marginBottom: '0.8rem', marginTop: 0, color: textColor }}>4. Acceptable Use</h2>
          <p>
            Use the Services responsibly and lawfully. Do not attempt to interfere with servers, exploit bugs, harass others, or engage in behavior that disrupts gameplay for other users.
          </p>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.2rem', marginBottom: '0.8rem', marginTop: 0, color: textColor }}>5. Data We Collect and How We Use It</h2>
          <p>
            We collect limited information—including but not limited to gameplay analytics (page views, puzzle completions, streak data), technical data (browser type, device type, approximate location from IP), and any answers or votes you submit during gameplay.
          </p>
          <p>We use this data to:</p>
          <ul style={{ margin: '0.5rem 0 1rem 1.5rem', padding: 0 }}>
            <li>Monitor and improve performance and stability;</li>
            <li>Track engagement and fix bugs;</li>
            <li>Support game features such as leaderboards or daily puzzles.</li>
          </ul>
          <p>We do not sell or rent user data to third parties.</p>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.2rem', marginBottom: '0.8rem', marginTop: 0, color: textColor }}>6. Cookies and Local Storage</h2>
          <p>
            We may store small amounts of data on your browser to remember game progress or preferences.
            By using our Services, you consent to this local storage.
            You can manage cookies in your browser settings, though some features may stop working if you disable them.
          </p>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.2rem', marginBottom: '0.8rem', marginTop: 0, color: textColor }}>7. Analytics and Third-Party Services</h2>
          <p>
            We use Google Analytics to understand how players interact with our Services.
            Google Analytics may collect data such as page views and session length through cookies or similar technologies.
            This information is processed under <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: linkColor, textDecoration: 'underline' }}>Google's Privacy Policy</a>.
            You can opt out via the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" style={{ color: linkColor, textDecoration: 'underline' }}>Google Analytics Opt-out Browser Add-on</a>.
          </p>
          <p>
            We do not share personally identifiable information with Google or other third parties. Analytics data is used only in aggregate to improve our games.
          </p>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.2rem', marginBottom: '0.8rem', marginTop: 0, color: textColor }}>8. Backend Game Data</h2>
          <p>
            Some games send information (such as answers, votes, or streak data) to backend systems operated by Mental Mint LLC.
            This data is used solely to provide game features and track in-game performance.
          </p>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.2rem', marginBottom: '0.8rem', marginTop: 0, color: textColor }}>9. Email Communications</h2>
          <p style={{ marginBottom: '0.8rem', fontWeight: 600 }}>9.1 Types of Emails</p>
          <p>We send two types of emails to users who have opted in:</p>
          <p style={{ marginBottom: '0.5rem' }}><strong style={{ fontWeight: 600 }}>Game Reminder Emails:</strong></p>
          <ul style={{ margin: '0.5rem 0 1rem 1.5rem', padding: 0 }}>
            <li>Frequency: Daily or weekly, based on your preference</li>
            <li>Purpose: Remind you to play your favorite games and check out daily puzzles</li>
            <li>Content: Personalized based on games you've played recently</li>
          </ul>
          <p style={{ marginBottom: '0.5rem' }}><strong style={{ fontWeight: 600 }}>Marketing News Emails:</strong></p>
          <ul style={{ margin: '0.5rem 0 1rem 1.5rem', padding: 0 }}>
            <li>Frequency: Occasional (when we have news to share)</li>
            <li>Purpose: Announce new games, features, and updates</li>
            <li>Content: Information about Mental Mint Games products and services</li>
          </ul>
          
          <p style={{ marginBottom: '0.8rem', fontWeight: 600, marginTop: '1rem' }}>9.2 Managing Your Email Preferences</p>
          <ul style={{ margin: '0.5rem 0 1rem 1.5rem', padding: 0 }}>
            <li>You can choose your email frequency when you sign up (daily, weekly, or never for reminders)</li>
            <li>You can opt in or out of marketing emails separately</li>
            <li>You may unsubscribe or manage your email preferences at any time via the link in our emails</li>
            <li>Changes to your preferences take effect immediately</li>
          </ul>

          <p style={{ marginBottom: '0.8rem', fontWeight: 600, marginTop: '1rem' }}>9.3 Email Sender Addresses</p>
          <ul style={{ margin: '0.5rem 0 1rem 1.5rem', padding: 0 }}>
            <li>Transactional emails (magic links, account notifications): info@mentalmintgames.com</li>
            <li>Marketing emails (reminders, news): notifications@mentalmintgames.com</li>
          </ul>

          <p style={{ marginBottom: '0.8rem', fontWeight: 600, marginTop: '1rem' }}>9.4 Compliance</p>
          <p>Our email practices comply with CAN-SPAM Act requirements, including:</p>
          <ul style={{ margin: '0.5rem 0 1rem 1.5rem', padding: 0 }}>
            <li>Clear unsubscribe links in every email</li>
            <li>Accurate sender information</li>
            <li>Honest subject lines</li>
            <li>Prompt processing of unsubscribe requests (within 10 business days)</li>
          </ul>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.2rem', marginBottom: '0.8rem', marginTop: 0, color: textColor }}>10. Children's Privacy</h2>
          <p>
            Our games are designed for general audiences, including children.
            We do not knowingly collect personal information such as full names or contact details.
            If a parent or guardian believes their child has provided us with such information, they may contact us and we will delete it.
          </p>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.2rem', marginBottom: '0.8rem', marginTop: 0, color: textColor }}>11. Data Security</h2>
          <p>
            We use reasonable technical and organizational measures to protect collected data.
            However, no online service is completely secure, and we cannot guarantee absolute protection.
          </p>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.2rem', marginBottom: '0.8rem', marginTop: 0, color: textColor }}>12. Disclaimer and Limitation of Liability</h2>
          <p>
            The Services are provided "as is" and "as available."
            To the fullest extent allowed by law, Mental Mint LLC disclaims all warranties, express or implied.
            We are not liable for indirect, incidental, or consequential damages arising from your use of the Services.
            Your sole remedy is to stop using them.
          </p>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.2rem', marginBottom: '0.8rem', marginTop: 0, color: textColor }}>13. Changes to These Terms</h2>
          <p>
            We may update these Terms or this Policy periodically.
            The latest version will always appear on this page with a "Last Updated" date.
            Your continued use after any update constitutes acceptance.
          </p>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.2rem', marginBottom: '0.8rem', marginTop: 0, color: textColor }}>14. Governing Law</h2>
          <p>
            These Terms are governed by the laws of the State of Washington, USA, without regard to conflict-of-law principles.
            Any disputes shall be brought exclusively in the state or federal courts located in King County, Washington.
          </p>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.2rem', marginBottom: '0.8rem', marginTop: 0, color: textColor }}>15. Contact Us</h2>
          <p>Questions or requests may be sent to:</p>
          <p>
            <strong style={{ fontWeight: 600 }}>Mental Mint LLC</strong><br />
            Email: <a href="mailto:nico@mentalmintgames.com" style={{ color: linkColor, textDecoration: 'underline' }}>nico@mentalmintgames.com</a>
          </p>
        </section>
      </div>
    </div>
  );
};
